<script setup>
import { ref, onMounted, computed } from 'vue'
import bg from '@/assets/img/contact/contact_bg.svg'
import bgElem1 from '@/assets/img/contact/contact_bg-elem-1.png'
import { getMain } from '@/api/page-main'
import customization from '@/assets/yandex-map-style.json'

// Декоративные элементы
const bgElems = [{ src: bgElem1, cx: 100, cy: 100, a: 50, dxr: true, dyr: true }]

// Контакты
const phone = ref('')
const cleanPhoneNumber = computed(() => phone.value.replace(/\D/g, '').replace(/^8/, '7'))
const mail = ref('')
const telegram = ref('')
const address = ref('')

// Центр карты
const LOCATION = { center: [37.636581, 55.720945], zoom: 16 }

onMounted(async () => {
	try {
		const { data } = await getMain()
		phone.value = data.contacts.phone_number
		mail.value = data.contacts.email
		telegram.value = data.contacts.telegram
		address.value = data.contacts.address
	} catch (err) {
		console.warn('Ошибка при загрузке данных:', err)
	}

	await loadYandexMaps()
	const ymaps3 = window.ymaps3
	await ymaps3.ready

	const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer, YMapMarker } = ymaps3

	// Создаём карту
	const map = new YMap(
		document.getElementById('yandex-map'),
		{
			location: LOCATION,
			mode: 'vector',
		},
		[new YMapDefaultSchemeLayer({ customization }), new YMapDefaultFeaturesLayer({})],
	)

	// Создаём HTML элемент для кастомного маркера
	const markerElement = document.createElement('div')
	markerElement.className = 'marker-class'

	// Создаём маркер с кастомным элементом
	const marker = new YMapMarker(
		{
			coordinates: LOCATION.center,
			draggable: false,
			mapFollowsOnDrag: true,
		},
		markerElement,
	)

	// Добавляем обработчик клика для показа попапа
	markerElement.addEventListener('click', () => {
		showPopup(map, LOCATION.center, address.value || 'Адрес не указан')
	})

	// Добавляем маркер на карту
	map.addChild(marker)
})

// Попап
function showPopup(map, coords, text) {
	// Убираем старый попап
	const old = document.querySelector('.custom-popup')
	if (old) {
		old.remove()
		return
	}

	// Создаём HTML элемент попапа
	const popup = document.createElement('div')
	popup.className = 'custom-popup'
	popup.innerHTML = `<div class="popup-content">${text}</div>`

	// Привязываем попап как маркер
	const { YMapMarker } = window.ymaps3
	const popupMarker = new YMapMarker({ coordinates: coords }, popup)

	map.addChild(popupMarker)
}

// Загрузка API
function loadYandexMaps() {
	return new Promise((resolve, reject) => {
		if (window.ymaps3) return resolve()
		const script = document.createElement('script')
		const apiKey = import.meta.env.VITE_MAP_API_KEY
		script.src = `https://api-maps.yandex.ru/v3/?apikey=${apiKey}&lang=ru_RU`
		script.onload = resolve
		script.onerror = reject
		document.head.appendChild(script)
	})
}
</script>

<style lang="scss">
.marker-class {
	background-color: var(--primary-color);
	border-radius: 50%;
	width: 3rem;
	height: 3rem;
	transform: translate(-50%, -50%);
	font-weight: bold;
	cursor: pointer;
	position: relative;

	&::before {
		content: '';
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
		border: 2px solid var(--primary-color);
		border-radius: inherit;
		width: 150%;
		height: 150%;
		animation: markerscale 3s linear infinite;
	}

	@keyframes markerscale {
		0% {
			transform: translate(-50%, -50%) scale(0);
			opacity: 1;
		}
		100% {
			transform: translate(-50%, -50%) scale(1.5);
			opacity: 0;
		}
	}
}

.custom-popup {
	background: #fff;
	padding: 0.8rem 1.2rem;
	width: 20rem;
	text-align: center;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	transform: translate(-50%, -130%);
	position: relative;
	pointer-events: auto;
	color: #000;
	font-weight: 300;
	font-size: 1.4rem;
}
</style>

<template>
	<section class="contact" id="contact">
		<div
			class="contact__bg"
			v-parallax
			:data-prlx-cx="1000"
			:data-prlx-cy="1000"
			:data-prlx-a="50"
			:data-prlx-dxr="true"
			:data-prlx-dyr="true"
		>
			<img :src="bg" alt="" />
		</div>

		<div class="contact__container">
			<div class="contact__wrapper">
				<div class="contact__desc">
					<h2>Контакты</h2>

					<ul class="contact__list">
						<li v-if="phone">
							<a :href="`tel:${cleanPhoneNumber}`"> {{ phone }}</a>
						</li>
						<li v-if="telegram">
							<a
								:href="'https://t.me/' + telegram.replace('@', '')"
								rel="nofollow noopener"
								target="_blank"
							>
								{{ telegram }}
							</a>
						</li>
						<li v-if="mail">
							<a :href="`mailto:${mail}`"> {{ mail }}</a>
						</li>
						<li v-if="address">
							{{ address }}
						</li>
					</ul>

					<button type="button" class="btn _btn-primary" data-popup="#feedbackMessage">
						<div class="btn__text">Напишите нам</div>
						<svg
							class="btn__shadow"
							preserveAspectRatio="none"
							viewBox="0 0 313 140"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M313 20.0118L293 0L293 120L313 140L313 20.0118Z"
								fill="#925566"
							/>
							<path d="M20 140L0 120H293L313 140H20Z" fill="#BA5F79" />
						</svg>
					</button>
				</div>

				<div class="contact__picture">
					<div id="yandex-map" class="contact__map"></div>

					<div
						v-for="(image, index) in bgElems"
						:key="index"
						:class="`contact__bg-elem contact__bg-elem-${index + 1}`"
						v-parallax
						:data-prlx-cx="image.cx"
						:data-prlx-cy="image.cy"
						:data-prlx-a="image.a"
						:data-prlx-dxr="image.dxr ? '' : null"
						:data-prlx-dyr="image.dyr ? '' : null"
					>
						<img :src="image.src" alt="" />
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<style scoped lang="scss">
.contact {
	position: relative;
	padding: 12rem 0 18.9rem;

	@media (max-width: 991.98px) {
		padding: 5.7rem 0 3.2rem;
	}

	// .contact__bg
	&__bg {
		position: absolute;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		z-index: -1;

		@media (max-width: 991.98px) {
			display: none;
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	// .contact__container
	&__container {
	}

	// .contact__wrapper
	&__wrapper {
		display: flex;
		align-items: flex-start;
		position: relative;

		@media (max-width: 991.98px) {
			flex-direction: column;
			padding-bottom: 12.2rem;
		}
	}

	// .contact__desc
	&__desc {
		width: 100%;

		@media (min-width: 991.98px) {
			max-width: 68.7rem;
			margin-right: 15.6rem;
		}

		.btn {
			@media (min-width: 991.98px) {
				margin-top: 6.4rem;
				padding-right: 2.6rem;
			}

			@media (max-width: 991.98px) {
				position: absolute;
				bottom: 0;
				left: 0;
				width: 100%;

				&__text {
					font-size: 2.1rem;
					padding: 3.2rem 3rem;
				}
			}
		}
	}

	// .contact__list
	&__list {
		margin-top: 5.1rem;

		@media (min-width: 991.98px) {
			max-width: 46.4rem;
		}

		@media (max-width: 991.98px) {
			margin-top: 3.6rem;
		}

		li {
			font-weight: 300;
			font-size: 2.4rem;
			// line-height: 3.2rem;
			line-height: 135%;
			letter-spacing: 0.02em;

			@media (max-width: 991.98px) {
				font-weight: 300;
				font-size: 1.6rem;
			}

			&:not(:last-child) {
				margin-bottom: 1.2rem;

				@media (max-width: 991.98px) {
					margin-bottom: 1.2rem;
				}
			}

			&:last-child {
				margin-top: 3.5rem;

				@media (max-width: 991.98px) {
					margin-top: 1.8rem;
				}
			}

			a {
				font-weight: 300;
				font-size: 4.4rem;
				// line-height: 4.7rem;
				line-height: 108%;
				text-transform: uppercase;
				color: #fff;
				transition: color 0.3s ease 0s;

				@media (max-width: 991.98px) {
					font-size: 2.2rem;
				}

				@media (any-hover: hover) {
					&:hover {
						color: var(--primary-color);
					}
				}

				&[href^='tel:'] {
					font-size: 4.4rem;
					color: #f27699;

					@media (max-width: 991.98px) {
						font-size: 2.6rem;
					}

					@media (any-hover: hover) {
						&:hover {
							color: #fff;
						}
					}
				}
			}
		}
	}

	// .contact__picture
	&__picture {
		position: relative;
		margin-top: 5.3rem;

		@media (max-width: 991.98px) {
			margin-top: 3.2rem;
		}

		@media (any-hover: hover) {
			&:hover {
				.contact__img-front {
					transform: scale(1.5);
					opacity: 0;
				}
				.contact__img-back {
				}
			}
		}
	}

	&__map {
		width: 59.6rem;
		height: 59.6rem;
		overflow: hidden;
		position: relative;

		@media (max-width: 991.98px) {
			width: 32rem;
			height: 32rem;
		}
	}

	&__bg-elem {
		position: absolute;
		z-index: 10;
		pointer-events: none;

		&-1 {
			width: 42.1rem;
			height: 42.1rem;
			top: -34%;
			right: -30%;

			@media (max-width: 991.98px) {
				width: 20.4rem;
				height: 20.4rem;
				top: -27%;
				right: -20%;
			}
		}
	}
}
</style>
